/**
 * Storage utility functions that integrate with existing useLocalStorage and useSessionStorage hooks
 * Includes fallback behavior for embedded content where storage APIs might be restricted
 */

const IS_SERVER = typeof window === 'undefined'
const BREAKTIME_LOCAL = `___btm_local`
const BREAKTIME_SESSION = `___btm_session`

/**
 * Safe localStorage operations with fallback to window storage
 */
export const localStorageUtils = {
  get: <T>(key: string, defaultValue: T): T => {
    if (IS_SERVER) return defaultValue

    try {
      const item = window.localStorage.getItem(key)
      if (item === null) return defaultValue

      return JSON.parse(item) as T
    } catch (error) {
      console.warn(`Error reading localStorage key "${key}":`, error)

      // Fallback to window storage
      if (
        typeof window !== 'undefined' &&
        window[BREAKTIME_LOCAL] &&
        window[BREAKTIME_LOCAL][key]
      ) {
        try {
          return JSON.parse(window[BREAKTIME_LOCAL][key]) as T
        } catch (parseError) {
          console.warn(
            `Error parsing fallback localStorage key "${key}":`,
            parseError,
          )
        }
      }

      return defaultValue
    }
  },

  set: <T>(key: string, value: T): void => {
    if (IS_SERVER) {
      console.warn(`Tried setting localStorage key "${key}" on server`)
      return
    }

    const serializedValue = JSON.stringify(value)

    try {
      window.localStorage.setItem(key, serializedValue)
    } catch (error) {
      console.warn(`Error setting localStorage key "${key}":`, error)

      // Fallback to window storage
      if (typeof window !== 'undefined') {
        if (window[BREAKTIME_LOCAL]) {
          window[BREAKTIME_LOCAL][key] = serializedValue
        } else {
          window[BREAKTIME_LOCAL] = {}
          window[BREAKTIME_LOCAL][key] = serializedValue
        }
      }
    }
  },

  remove: (key: string): void => {
    if (IS_SERVER) {
      console.warn(`Tried removing localStorage key "${key}" on server`)
      return
    }

    try {
      window.localStorage.removeItem(key)
    } catch (error) {
      console.warn(`Error removing localStorage key "${key}":`, error)
    }

    // Also remove from fallback storage
    if (
      typeof window !== 'undefined' &&
      window[BREAKTIME_LOCAL] &&
      window[BREAKTIME_LOCAL][key]
    ) {
      delete window[BREAKTIME_LOCAL][key]
    }
  },

  isAvailable: (): boolean => {
    if (IS_SERVER) return false

    try {
      const testKey = '__localStorage_test__'
      window.localStorage.setItem(testKey, 'test')
      window.localStorage.removeItem(testKey)
      return true
    } catch {
      return false
    }
  },
}

/**
 * Safe sessionStorage operations with fallback to window storage
 */
export const sessionStorageUtils = {
  get: <T>(key: string, defaultValue: T): T => {
    if (IS_SERVER) return defaultValue

    try {
      const item = window.sessionStorage.getItem(key)
      if (item === null) return defaultValue

      return JSON.parse(item) as T
    } catch (error) {
      console.warn(`Error reading sessionStorage key "${key}":`, error)

      // Fallback to window storage
      if (
        typeof window !== 'undefined' &&
        window[BREAKTIME_SESSION] &&
        window[BREAKTIME_SESSION][key]
      ) {
        try {
          return JSON.parse(window[BREAKTIME_SESSION][key]) as T
        } catch (parseError) {
          console.warn(
            `Error parsing fallback sessionStorage key "${key}":`,
            parseError,
          )
        }
      }

      return defaultValue
    }
  },

  set: <T>(key: string, value: T): void => {
    if (IS_SERVER) {
      console.warn(`Tried setting sessionStorage key "${key}" on server`)
      return
    }

    const serializedValue = JSON.stringify(value)

    try {
      window.sessionStorage.setItem(key, serializedValue)
    } catch (error) {
      console.warn(`Error setting sessionStorage key "${key}":`, error)

      // Fallback to window storage
      if (typeof window !== 'undefined') {
        if (window[BREAKTIME_SESSION]) {
          window[BREAKTIME_SESSION][key] = serializedValue
        } else {
          window[BREAKTIME_SESSION] = {}
          window[BREAKTIME_SESSION][key] = serializedValue
        }
      }
    }
  },

  remove: (key: string): void => {
    if (IS_SERVER) {
      console.warn(`Tried removing sessionStorage key "${key}" on server`)
      return
    }

    try {
      window.sessionStorage.removeItem(key)
    } catch (error) {
      console.warn(`Error removing sessionStorage key "${key}":`, error)
    }

    // Also remove from fallback storage
    if (
      typeof window !== 'undefined' &&
      window[BREAKTIME_SESSION] &&
      window[BREAKTIME_SESSION][key]
    ) {
      delete window[BREAKTIME_SESSION][key]
    }
  },

  isAvailable: (): boolean => {
    if (IS_SERVER) return false

    try {
      const testKey = '__sessionStorage_test__'
      window.sessionStorage.setItem(testKey, 'test')
      window.sessionStorage.removeItem(testKey)
      return true
    } catch {
      return false
    }
  },
}
